/**
 * ЕО «Финансы» — модель блока «Способы оплаты» (PayShare) и бейджа строки «Отведённых игр» (без React и запросов).
 * Архив 1С, В8 (решение владельца 16.10): у игр 1С 2019–2023 вид оплаты неизвестен — вся сумма в game_reports.other.
 * Сервер (/finance/analytics) делит other: «Прочее» — «Друг.» отчётов V4, «Прочее (1С)» — игр 1С (utils/payOther1c.ts);
 * каждый рубль — в одной из двух частей. Прежние сегменты (Нал, Терминал, Перевод, Предоплата, Счёт) — как были, порядок тот же,
 * новые — после них. Проценты — от выручки периода, как раньше. Сдачу учитывает механизм отчёта (сумма сдачи и её источник;
 * total = оплаты − сдача), отдельной строки в легенде нет — решение владельца 16.10.
 */

export type PayKey = "cash" | "terminal" | "transfer" | "prepaid" | "invoice" | "other" | "other1c";

export interface PayTotals {
  revenue: number; cash: number; terminal: number; transfer: number; prepaid: number; invoice: number;
  /** «Друг.» отчётов V4 */ other: number;
  /** сумма игр 1С с неизвестным видом оплаты */ other1c: number;
}

export const PAY_LABEL: Record<PayKey, string> = {
  cash: "Нал", terminal: "Терминал", transfer: "Перевод", prepaid: "Предоплата", invoice: "Счёт",
  other: "Прочее", other1c: "Прочее (1С)",
};
const ORDER: readonly PayKey[] = ["cash", "terminal", "transfer", "prepaid", "invoice", "other", "other1c"];

export interface PaySeg { key: PayKey; label: string; v: number; pct: number }
export interface PayShareModel {
  items: PaySeg[];
  /** база процентов — выручка периода (как раньше; без неё — сумма сегментов) */
  total: number;
}

export function payShare(t: PayTotals): PayShareModel {
  const raw = ORDER.map((key) => ({ key, v: Number(t[key]) || 0 })).filter((x) => x.v > 0);
  const total = t.revenue || raw.reduce((s, x) => s + x.v, 0) || 1;
  const pct = (v: number) => Math.round((v / total) * 100);
  return {
    items: raw.map((x) => ({ ...x, label: PAY_LABEL[x.key], pct: pct(x.v) })),
    total,
  };
}

/** Строка «Отведённых игр» — поля как в /finance/games-list. */
export interface PayRow {
  cash?: number | null; terminal?: number | null; transfer?: number | null; prepaid_used?: number | null; invoice?: number | null;
  other?: number | null; is_1c?: number | boolean | null;
}
/** Короткие подписи бейджа: прежние пять + «Проч.» (V4) и «Проч. 1С». */
export const BADGE_LABEL: Record<PayKey, string> = {
  cash: "Нал", terminal: "Терм", transfer: "Перев", prepaid: "Предопл", invoice: "Счёт", other: "Проч.", other1c: "Проч. 1С",
};

/** Основной способ оплаты игры — с максимальной суммой; при равенстве — первый по порядку (V1:7697-7712). Нет оплат — null («—»). */
export function payBadge(g: PayRow): PayKey | null {
  const other: PayKey = g.is_1c ? "other1c" : "other";
  const opts: [PayKey, unknown][] = [
    ["cash", g.cash], ["terminal", g.terminal], ["transfer", g.transfer], ["prepaid", g.prepaid_used], ["invoice", g.invoice], [other, g.other],
  ];
  let best: PayKey | null = null; let bestV = 0;
  for (const [k, raw] of opts) { const v = Number(raw) || 0; if (v > bestV) { best = k; bestV = v; } }
  return best;
}
